import { useCallback, useMemo, useState } from 'react';
import { DropdownMenu } from 'radix-ui';
import { Typography, TypographyVariant, TypographyVariants } from '../Typography';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ChevronUp } from '../svgs';
import { CheckboxItem } from '../Checkbox';

export type DropdownOption = {
  id: string;
  label: string;
  value: string;
};

export interface DropdownProps {
  className?: string;
  label?: string;
  labelVariant?: TypographyVariants;

  options: DropdownOption[];
  valueVariant?: TypographyVariants;
  values: DropdownOption['value'][];
  emptyValue?: string;
  onValueChange: (option: DropdownOption, checked: boolean) => void;

  onWhite?: boolean;
  completed?: boolean;
  disabled?: boolean;
  status?: 'error' | 'success';
  placeholder?: string;
  multiple?: boolean;
}

const disabledVariant = [
  `text-[--inputfield-input-text-disabled]`,
  `bg-[--inputfield-input-bg-disabled]`,
  `hover:border-[transparent]`,
  `focus-within:border-[transparent]`,
  `!border-[transparent]`,
];

const DropdownContainerCVA = cva(
  [
    'flex',
    'gap-[--padding-zero]',
    `px-[--padding-sm]`,
    `py-[--padding-xs]`,
    `rounded-[--corner-xs]`,
    `border-[1px]`,
    `border-solid`,
    `border-[transparent]`,
    `hover:border-[--inputfield-input-border-active]`,
    `focus-within:border-[--inputfield-input-border-active]`,
    'font-primary',
    TypographyVariant.paragraph020,
  ],
  {
    variants: {
      onWhite: {
        true: 'bg-[--inputfield-input-bg-default]',
        false: 'bg-[--inputfield-input-bg-white]',
      },
      completed: {
        true: `!border-[--inputfield-input-border-active]`,
        false: '',
      },
      status: {
        error: '!border-[--inputfield-input-border-error]',
        success: '!border-[--inputfield-input-border-success]',
      },
      disabled: {
        true: disabledVariant,
        false: '',
      },
      multiple: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        status: 'error',
        className: disabledVariant,
      },
      {
        disabled: true,
        status: 'success',
        className: disabledVariant,
      },
    ],
  }
);

export const Dropdown = ({
  label,
  labelVariant = 'paragraph010',
  valueVariant = 'paragraph020',
  values,
  options,
  onWhite,
  disabled,
  completed,
  status,
  emptyValue,
  className,
  multiple,
  placeholder,
  onValueChange,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);

  const displayValue = useMemo(() => {
    const filteredOptions = options.filter(v => values.some(_v => _v === v.value));
    return filteredOptions.length === 0 ? emptyValue : filteredOptions.map(o => o.label).join(', ');
  }, [options, values, emptyValue]);

  const renderCheckboxItem = useCallback(
    (option: DropdownOption, checked: boolean) => {
      return (
        <DropdownMenu.CheckboxItem
          key={option.id}
          checked={checked}
          onSelect={e => e.preventDefault()}
          onCheckedChange={_checked => {
            onValueChange?.(option, _checked);
          }}
          className={twMerge([
            'p-[--padding-sm]',
            'data-[highlighted]:bg-[--inputfield-input-bg-default]',
            'data-[highlighted]:outline-none',
            'flex',
            'flex-1',
            'flex-row',
            'gap-[--padding-xs]',
            'items-center',
            'cursor-pointer',
          ])}
        >
          {multiple ? <CheckboxItem checked={checked} className="" /> : null}
          <Typography variant={'paragraph020'}>{option.label}</Typography>
        </DropdownMenu.CheckboxItem>
      );
    },
    [multiple, onValueChange]
  );

  const renderItem = useCallback(
    (option: DropdownOption, checked: boolean) => {
      return (
        <DropdownMenu.Item
          key={option.id}
          onClick={() => {
            onValueChange?.(option, !checked);
          }}
          className={twMerge([
            'p-[--padding-sm]',
            'data-[highlighted]:bg-[--inputfield-input-bg-default]',
            'data-[highlighted]:outline-none',
            'flex',
            'flex-1',
            'flex-row',
            'gap-[--padding-xs]',
            'items-center',
            'cursor-pointer',
          ])}
        >
          <Typography variant={'paragraph020'}>{option.label}</Typography>
        </DropdownMenu.Item>
      );
    },
    [onValueChange]
  );

  return (
    <div className={twMerge('flex min-w-0 flex-0 flex-col gap-[--padding-xs]', className)}>
      {label ? (
        <Typography as="label" variant={labelVariant}>
          {label}
        </Typography>
      ) : null}
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <div
            className={twMerge(
              DropdownContainerCVA({
                onWhite,
                disabled,
                completed,
                status,
              }),
              ['w-full items-center justify-start bg-[--inputfield-input-bg-default]']
            )}
          >
            <Typography
              className="flex-1 text-start text-ellipsis overflow-hidden whitespace-nowrap"
              variant={valueVariant}
            >
              {displayValue || placeholder}
            </Typography>
            <ChevronUp
              height={16}
              width={16}
              className={twMerge('scale-y-[-1] transition-transform', open ? 'rotate-180' : '')}
            />
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          {
            //* TODO inject the class in Portal
          }
          <DropdownMenu.Content
            style={{
              width: 'max(240px, var(--radix-dropdown-menu-trigger-width))',
            }}
            sideOffset={12}
            align="start"
            className="light components dt z-[200] max-h-[300px] overflow-y-auto rounded-[--corner-sm] bg-[--dropdown-menu-bg-default] shadow-md"
          >
            {options?.map(option => {
              const checked = values.some(v => v === option.value);
              return multiple ? renderCheckboxItem(option, checked) : renderItem(option, checked);
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};
